import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table"
import {
  deleteUserFromOrganization,
  inviteUserToOrganization,
  revokeInvite
} from "@/services/userService"
import {
  AlertDialogButtons,
  AlertDialogDescription,
  AlertDialogTitle
} from "@/constants/alert-dialog/alert-dialog"
import { UserTableRow } from "@/types/users"
import { AlertDialogButton } from "@/components/custom/AlertDialogButton/AlertDialogButton"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { UserStatus } from "@/constants/users/userStatus"
import { ButtonVariant } from "@/constants/button/button"
import { TableHeaderComponent } from "@/components/custom/Table/TableHeader"
import { EmptyTableRow } from "@/components/custom/Table/EmptyTableRow"
import { getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { ColumnMetaData, TableDataProps } from "@/types/types"
import { UserRole } from "@/constants/users/userRole"
import { CustomTableCell } from "@/components/custom/Table/TableCell"
import { TableCellType } from "@/constants/table/table"

export const DataTable = ({
  columns,
  data,
}: TableDataProps<UserTableRow>
) => {
  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const onDeleteUser = (userId: string) => {
    if (!token) return
    dispatch(deleteUserFromOrganization(token, userId))
      .catch((err: unknown) => { console.log(err) })
  }

  const onRevokeInvite = (userId: string) => {
    if (!token) return
    dispatch(revokeInvite(token, userId))
      .catch((err: unknown) => { console.log(err) })
  }

  const onReInvite = (user: UserTableRow) => {
    if (!token) return
    const data = {
      email: user.email,
      role: user.role.toLowerCase()
    }
    dispatch(inviteUserToOrganization(token, data))
      .catch((err: unknown) => { console.log(err) })
  }

  return (
    <div className="rounded-md border w-full">
      <Table>
        <TableHeaderComponent data={data} columns={columns} />
        <TableBody className="text-primary">
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="bg-primary-foreground hover:bg-border/5 hover:cursor-auto"
              >
                {row.getVisibleCells().map((cell) => {
                  if (cell.column.id === 'action') {
                    if (cell.row.original.status === UserStatus.INVITED) {
                      return (
                        <TableCell key={cell.id} className="w-40">
                          {userData?.role !== UserRole.USER && (
                            <>
                              <AlertDialogButton
                                actionButtonText={AlertDialogButtons.REVOKE}
                                dialogTitle={AlertDialogTitle.INVITATION_REVOKE}
                                dialogSubtitle={AlertDialogDescription.INVITATION_REVOKE}
                                onClick={() => { onRevokeInvite(row.original.id) }}
                                variant={ButtonVariant.DESTRUCTIVE}
                                isTableActionButton
                              />
                              <AlertDialogButton
                                actionButtonText={AlertDialogButtons.RE_INVITE}
                                dialogTitle={AlertDialogTitle.INVITATION_RESEND}
                                dialogSubtitle={AlertDialogDescription.INVITATION_RESEND}
                                onClick={() => { onReInvite(row.original) }}
                                variant={ButtonVariant.DEFAULT}
                                isTableActionButton
                              />
                            </>
                          )}
                        </TableCell>
                      )
                    } else {
                      return (
                        <TableCell key={cell.id} className="w-40">
                          {!cell.row.original.isMyself && userData?.role !== UserRole.USER && (
                            <AlertDialogButton
                              actionButtonText={AlertDialogButtons.REMOVE}
                              dialogTitle={AlertDialogTitle.REMOVE_USER}
                              dialogSubtitle={AlertDialogDescription.REMOVE_USER}
                              onClick={() => { onDeleteUser(row.original.id) }}
                              variant={ButtonVariant.DESTRUCTIVE}
                              isTableActionButton
                            />
                          )}
                        </TableCell>
                      )
                    }
                  } else {
                    return (
                      <CustomTableCell
                        key={cell.id}
                        cell={cell}
                        onClick={() => {
                          if ((cell.column.columnDef.meta as ColumnMetaData)?.type === TableCellType.LINK_BUTTON) {
                            navigate(`/users/edit/${cell.getContext().row.original.id}`)
                          }
                        }}
                      />
                    )
                  }
                })}
              </TableRow>
            ))
          ) : (
            <EmptyTableRow numberOfColumns={columns.length} />
          )}
        </TableBody>
      </Table>
    </div>
  )
}
